import React, { useState, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, Link } from "react-router-dom";
import axios from "axios";
import MonthlyCalendar from "./components/MonthlyCalendar";
import ManageBudgets from "./components/ManageBudgets";
import ForgotPassword from "./components/ForgotPassword";
import PasswordReset from "./components/PasswordReset";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./components/ui/card";
import { Badge } from "./components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { Calendar } from "./components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Textarea } from "./components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "./components/ui/dialog";
import { Alert, AlertDescription } from "./components/ui/alert";
import { Label } from "./components/ui/label";
import { Separator } from "./components/ui/separator";
import { CalendarIcon, Clock, Users, FolderOpen, Plus, LogOut, BarChart3, Edit, Trash2, ChevronLeft, ChevronRight, RotateCcw, AlertTriangle } from "lucide-react";
import { format } from "date-fns";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Token management
const getToken = () => localStorage.getItem("token");
const setToken = (token) => localStorage.setItem("token", token);
const removeToken = () => localStorage.removeItem("token");

// API client setup with enhanced security
const apiClient = axios.create({
  baseURL: API,
  withCredentials: true, // Enable cookies for httpOnly tokens
});

apiClient.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Enhanced error handling for security
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid - logout user
      removeToken();
      localStorage.removeItem("user");
      window.location.reload();
    }
    if (error.response?.status === 429) {
      alert("Too many requests. Please wait a moment before trying again.");
    }
    return Promise.reject(error);
  }
);

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const response = await apiClient.post("/auth/login", { email, password });
      
      // Store token and user data
      localStorage.setItem("token", response.data.token);
      localStorage.setItem("user", JSON.stringify(response.data.user));
      
      onLogin(response.data.user);
    } catch (err) {
      if (err.response?.status === 429) {
        setError("Too many login attempts. Please wait a minute and try again.");
      } else {
        setError(err.response?.data?.detail || "Login failed");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50">
      <Card className="w-full max-w-md shadow-lg">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold text-center">Project Hours</CardTitle>
          <CardDescription className="text-center">
            Sign in to your time tracking account
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="your.email@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password-input"
              />
            </div>
            <Button 
              type="submit" 
              className="w-full" 
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? "Signing in..." : "Sign In"}
            </Button>
          </form>
          
          <div className="mt-4 text-center">
            <Link 
              to="/forgot-password" 
              className="text-sm text-blue-600 hover:text-blue-800 underline"
            >
              Forgot your password?
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const AdminDashboard = ({ user, onLogout }) => {
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [timeEntries, setTimeEntries] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Form states
  const [newClient, setNewClient] = useState({ name: "" });
  const [newProject, setNewProject] = useState({ name: "", client_id: "" });
  const [newUser, setNewUser] = useState({ name: "", email: "", role: "team_member", password: "" });
  
  // Assignment states
  const [assigningProjectManager, setAssigningProjectManager] = useState(null);
  
  // Export states
  const [exportMonth, setExportMonth] = useState(new Date().getMonth() + 1);
  const [exportYear, setExportYear] = useState(new Date().getFullYear());
  const [exportData, setExportData] = useState(null);
  
  // Edit states
  const [editingClient, setEditingClient] = useState(null);
  const [editingProject, setEditingProject] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  
  // Sorting and filtering states
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  const [clientFilter, setClientFilter] = useState('');
  const [projectFilter, setProjectFilter] = useState('');
  
  // Monthly budgets state
  const [monthlyBudgets, setMonthlyBudgets] = useState([]);
  
  // User management states
  const [viewingUserHours, setViewingUserHours] = useState(null);
  const [assigningUser, setAssigningUser] = useState(null);
  
  // Report filtering
  const [reportFilter, setReportFilter] = useState('');
  
  // Month selection for all admin views
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  // Finalized timesheets state
  const [finalizedTimesheets, setFinalizedTimesheets] = useState([]);
  
  const monthNames = ["January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"];

  const fetchData = async () => {
    try {
      const [clientsRes, projectsRes, entriesRes, usersRes] = await Promise.all([
        apiClient.get("/clients"),
        apiClient.get("/projects"),
        apiClient.get("/time-entries"),
        apiClient.get("/users")
      ]);
      setClients(clientsRes.data);
      setProjects(projectsRes.data);
      setTimeEntries(entriesRes.data);
      setUsers(usersRes.data);
    } catch (err) {
      console.error("Error fetching data:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchMonthlyBudgets = async () => {
    try {
      const budgetsRes = await apiClient.get(`/monthly-budgets/${selectedYear}/${selectedMonth}`);
      setMonthlyBudgets(budgetsRes.data);
    } catch (err) {
      console.error("Error fetching monthly budgets:", err);
      setMonthlyBudgets([]);
    }
  };

  useEffect(() => {
    fetchData();
    fetchFinalizedTimesheets();
  }, []);

  useEffect(() => {
    fetchMonthlyBudgets();
  }, [selectedMonth, selectedYear]);

  const createClient = async () => {
    try {
      await apiClient.post("/clients", newClient);
      setNewClient({ name: "" });
      fetchData();
    } catch (err) {
      console.error("Error creating client:", err);
    }
  };

  const createProject = async () => {
    try {
      await apiClient.post("/projects", newProject);
      setNewProject({ name: "", client_id: "" });
      fetchData();
    } catch (err) {
      console.error("Error creating project:", err);
    }
  };

  const createUser = async () => {
    try {
      await apiClient.post("/auth/register", newUser);
      setNewUser({ name: "", email: "", role: "team_member", password: "" });
      fetchData();
    } catch (err) {
      console.error("Error creating user:", err);
    }
  };

  const initSampleData = async () => {
    try {
      await apiClient.post("/init-sample-data");
      fetchData();
    } catch (err) {
      console.error("Error initializing sample data:", err);
    }
  };

  const createBackup = async () => {
    try {
      const response = await apiClient.post("/admin/automated-backup");
      
      alert(`✅ Backup Created Successfully!\n\n` +
            `Backup ID: ${response.data.backup_id}\n` +
            `Records: ${response.data.record_count}\n` +
            `Timestamp: ${new Date(response.data.timestamp).toLocaleString()}`);
            
    } catch (err) {
      console.error("Error creating backup:", err);
      const errorMsg = err.response?.data?.detail || "Error creating backup. Please try again.";
      alert(`Backup failed: ${errorMsg}`);
    }
  };

  const exportAllData = async () => {
    try {
      const response = await apiClient.get("/admin/export-data");
      
      // Create downloadable file
      const dataStr = JSON.stringify(response.data, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      
      // Trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = `timetracker-backup-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      alert(`✅ Data Export Complete!\n\n` +
            `Total Records: ${Object.values(response.data.metadata.record_counts).reduce((a, b) => a + b, 0)}\n` +
            `File downloaded to your computer.`);
            
    } catch (err) {
      console.error("Error exporting data:", err);
      const errorMsg = err.response?.data?.detail || "Error exporting data. Please try again.";
      alert(`Export failed: ${errorMsg}`);
    }
  };

  const listBackups = async () => {
    try {
      const response = await apiClient.get("/admin/list-backups");
      
      if (response.data.backups.length === 0) {
        alert("No backups found. Create your first backup using the 'Create Backup' button.");
        return;
      }
      
      let message = `📋 AVAILABLE BACKUPS (${response.data.total_count})\n\n`;
      response.data.backups.forEach((backup, index) => {
        const date = new Date(backup.created_at).toLocaleString();
        message += `${index + 1}. ${date}\n`;
        message += `   ID: ${backup.id.substring(0, 8)}...\n`;
        message += `   Records: ${backup.record_count}\n\n`;
      });
      
      message += "Use the 'Restore Backup' button to restore from a specific backup.";
      alert(message);
      
    } catch (err) {
      console.error("Error listing backups:", err);
      const errorMsg = err.response?.data?.detail || "Error listing backups. Please try again.";
      alert(`List backups failed: ${errorMsg}`);
    }
  };

  const assignProjectManagers = async (projectId, managerIds) => {
    try {
      await apiClient.put(`/projects/${projectId}/assign-managers`, {
        assigned_user_ids: managerIds
      });
      
      alert("Project managers assigned successfully!");
      fetchData(); // Refresh data
      setAssigningProjectManager(null);
      
    } catch (err) {
      console.error("Error assigning project managers:", err);
      const errorMsg = err.response?.data?.detail || "Error assigning project managers. Please try again.";
      alert(`Assignment failed: ${errorMsg}`);
    }
  };

  const restoreBackup = async () => {
    try {
      // First, get the list of available backups
      const response = await apiClient.get("/admin/list-backups");
      
      if (response.data.backups.length === 0) {
        alert("No backups found. Create a backup first using the 'Create Backup' button.");
        return;
      }

      // Create a selection dialog
      let selectionMessage = `🔄 SELECT BACKUP TO RESTORE\n\n`;
      selectionMessage += `⚠️ WARNING: This will REPLACE current data with backup data!\n\n`;
      selectionMessage += `Available backups:\n`;
      
      response.data.backups.forEach((backup, index) => {
        const date = new Date(backup.created_at).toLocaleString();
        selectionMessage += `${index + 1}. ${date} (${backup.record_count} records)\n`;
      });
      
      selectionMessage += `\nEnter the number of the backup to restore (1-${response.data.backups.length}):`;
      
      const selection = window.prompt(selectionMessage);
      
      if (!selection) {
        return; // User cancelled
      }
      
      const backupIndex = parseInt(selection) - 1;
      if (backupIndex < 0 || backupIndex >= response.data.backups.length || isNaN(backupIndex)) {
        alert("Invalid backup selection. Operation cancelled.");
        return;
      }
      
      const selectedBackup = response.data.backups[backupIndex];
      const backupDate = new Date(selectedBackup.created_at).toLocaleString();
      
      // Confirmation dialog
      const confirmMessage = `⚠️ FINAL CONFIRMATION\n\n` +
                           `You are about to restore:\n` +
                           `📅 Backup from: ${backupDate}\n` +
                           `📊 Records: ${selectedBackup.record_count}\n\n` +
                           `🚨 This will REPLACE all current data!\n` +
                           `(Admin account will be preserved)\n\n` +
                           `Are you absolutely sure you want to proceed?`;
      
      if (!window.confirm(confirmMessage)) {
        return;
      }
      
      // Require typing confirmation
      const confirmationText = window.prompt("To proceed, type exactly: RESTORE BACKUP");
      if (confirmationText !== "RESTORE BACKUP") {
        alert("Confirmation text incorrect. Operation cancelled.");
        return;
      }
      
      // Require admin password
      const adminPassword = window.prompt("Enter your admin password to confirm restore:");
      if (!adminPassword) {
        alert("Password required. Operation cancelled.");
        return;
      }
      
      // Proceed with restore
      alert("🔄 Restore in progress... This may take a moment.");
      
      const restoreResponse = await apiClient.post(`/admin/restore-backup/${selectedBackup.id}`, {
        confirmation_text: "RESTORE BACKUP",
        admin_password: adminPassword
      });
      
      if (restoreResponse.data?.summary) {
        const summary = restoreResponse.data.summary;
        const imported = summary.imported_counts;
        
        let successMessage = `✅ BACKUP RESTORE COMPLETED!\n\n`;
        successMessage += `📈 Data imported:\n`;
        Object.entries(imported).forEach(([type, count]) => {
          if (count > 0) {
            successMessage += `• ${type.replace('_', ' ')}: ${count}\n`;
          }
        });
        
        if (summary.errors && summary.errors.length > 0) {
          successMessage += `\n⚠️ Minor issues encountered:\n`;
          summary.errors.slice(0, 3).forEach(error => {
            successMessage += `• ${error}\n`;
          });
        }
        
        successMessage += `\nTotal imported: ${Object.values(imported).reduce((a, b) => a + b, 0)} records`;
        
        alert(successMessage);
        fetchData(); // Refresh the dashboard
      } else {
        alert("✅ Backup restored successfully! Refreshing dashboard...");
        fetchData();
      }
      
    } catch (err) {
      console.error("Error restoring backup:", err);
      const errorMsg = err.response?.data?.detail || "Error restoring backup. Please try again.";
      alert(`Restore failed: ${errorMsg}`);
    }
  };

  const validateDeployment = async () => {
    try {
      const response = await apiClient.post("/admin/deployment-validation");
      
      const results = response.data;
      let message = `🔍 DEPLOYMENT VALIDATION RESULTS\n`;
      message += `Environment: ${results.environment}\n`;
      message += `Overall Status: ${results.overall_status.toUpperCase()}\n\n`;
      
      message += `Test Results:\n`;
      Object.entries(results.tests).forEach(([test, result]) => {
        const icon = result.status === 'pass' ? '✅' : result.status === 'fail' ? '❌' : '⏳';
        message += `${icon} ${test.replace('_', ' ').toUpperCase()}: ${result.status}\n`;
        if (result.details) {
          message += `   Details: ${result.details}\n`;
        }
      });
      
      if (results.failed_tests && results.failed_tests.length > 0) {
        message += `\n⚠️ Failed Tests: ${results.failed_tests.join(', ')}`;
      }
      
      alert(message);
      
    } catch (err) {
      console.error("Error validating deployment:", err);
      const errorMsg = err.response?.data?.detail || "Error validating deployment. Please try again.";
      alert(`Deployment validation failed: ${errorMsg}`);
    }
  };

  const cleanupSampleData = async () => {
    // First confirmation
    if (!window.confirm("⚠️ EXTREME WARNING: This will delete ALL DATA including:\n\n• ALL users (except admin)\n• ALL time entries\n• ALL clients and projects\n• ALL monthly budgets\n• ALL password reset tokens\n\nOnly the admin account will remain!\n\nThis action cannot be undone. Are you absolutely sure?")) {
      return;
    }
    
    // Require typing confirmation
    const confirmationText = window.prompt("To proceed, type exactly: DELETE ALL DATA");
    if (confirmationText !== "DELETE ALL DATA") {
      alert("Confirmation text incorrect. Operation cancelled.");
      return;
    }
    
    // Require admin password
    const adminPassword = window.prompt("Enter your admin password to confirm:");
    if (!adminPassword) {
      alert("Password required. Operation cancelled.");
      return;
    }
    
    try {
      const response = await apiClient.post("/admin/cleanup-sample-data", {
        confirmation_text: confirmationText,
        admin_password: adminPassword
      });
      
      if (response.data?.deleted) {
        const deleted = response.data.deleted;
        alert(`Database cleanup completed!\n\n` +
              `• Users deleted: ${deleted.users}\n` +
              `• Time entries deleted: ${deleted.time_entries}\n` +
              `• Clients deleted: ${deleted.clients}\n` +
              `• Projects deleted: ${deleted.projects}\n` +
              `• Monthly budgets deleted: ${deleted.monthly_budgets}\n` +
              `• Password resets deleted: ${deleted.password_resets}\n` +
              `• Old audit logs deleted: ${deleted.audit_logs}`);
      }
      
      fetchData();
    } catch (err) {
      console.error("Error cleaning up database:", err);
      const errorMsg = err.response?.data?.detail || "Error cleaning up database. Please try again.";
      alert(errorMsg);
    }
  };

  const productionReset = async () => {
    // Multiple confirmations for production reset
    if (!window.confirm("🚨 PRODUCTION RESET WARNING 🚨\n\nThis will PERMANENTLY DELETE:\n• ALL users (except admin)\n• ALL projects, clients, time entries\n• ALL budgets and submissions\n• ALL backup files\n• ALL audit logs\n\nThis is intended for CLEAN PRODUCTION DEPLOYMENT only!\n\nProceed?")) {
      return;
    }
    
    if (!window.confirm("⚠️ FINAL WARNING ⚠️\n\nThis action will create a COMPLETELY EMPTY database with only the admin user remaining.\n\nThis cannot be undone!\n\nAre you absolutely certain?")) {
      return;
    }
    
    // Require typing confirmation
    const confirmationText = window.prompt("To proceed, type exactly: PRODUCTION RESET");
    if (confirmationText !== "PRODUCTION RESET") {
      alert("Confirmation text incorrect. Operation cancelled.");
      return;
    }
    
    try {
      const response = await apiClient.post("/admin/production-reset");
      
      if (response.data?.deleted_counts) {
        const deleted = response.data.deleted_counts;
        alert(`🚨 PRODUCTION RESET COMPLETED! 🚨\n\n` +
              `Database is now completely clean with only admin user remaining.\n\n` +
              `Deleted:\n` +
              `• Users: ${deleted.users}\n` +
              `• Clients: ${deleted.clients}\n` +
              `• Projects: ${deleted.projects}\n` +
              `• Time entries: ${deleted.time_entries}\n` +
              `• Monthly budgets: ${deleted.monthly_budgets}\n` +
              `• Submissions: ${deleted.monthly_submissions}\n` +
              `• Backups: All backup files removed\n\n` +
              `Production environment is ready for clean deployment.`);
      }
      
      // Refresh the page to show clean state
      window.location.reload();
    } catch (err) {
      console.error("Error performing production reset:", err);
      const errorMsg = err.response?.data?.detail || "Production reset failed. Please try again.";
      alert(`Production reset failed: ${errorMsg}`);
    }
  };

  const exportMonthlyData = async () => {
    try {
      // Generate detailed export data with user breakdown
      const exportRows = [];
      
      // Header row - Added User column
      exportRows.push(['Client', 'Project', 'User', 'Monthly Actual (hours)', 'Monthly Budget (hours)', 'Remaining (hours)', 'Status']);
      
      // Group data by client -> project -> user
      const groupedData = {};
      
      // Process each time entry for the selected month
      timeEntries
        .filter(entry => 
          new Date(entry.date).getMonth() + 1 === selectedMonth &&
          new Date(entry.date).getFullYear() === selectedYear
        )
        .forEach(entry => {
          const project = projects.find(p => p.id === entry.project_id);
          const user = users.find(u => u.id === entry.user_id);
          
          if (project && user) {
            const clientName = project.client_name;
            const projectName = project.name;
            const userName = user.name;
            const key = `${clientName}|${projectName}|${userName}`;
            
            if (!groupedData[key]) {
              groupedData[key] = {
                clientName,
                projectName,
                userName,
                projectId: project.id,
                totalHours: 0
              };
            }
            
            groupedData[key].totalHours += entry.hours;
          }
        });
      
      // Convert grouped data to export rows
      Object.values(groupedData).forEach(item => {
        // Get monthly budget for this project
        const monthlyBudget = monthlyBudgets.find(b => 
          b.project_id === item.projectId && 
          b.year === selectedYear && 
          b.month === selectedMonth
        );
        const budgetHours = monthlyBudget ? monthlyBudget.budget_hours : null;
        
        // Calculate project total hours for budget comparison
        const projectTotalHours = timeEntries
          .filter(entry => 
            entry.project_id === item.projectId &&
            new Date(entry.date).getMonth() + 1 === selectedMonth &&
            new Date(entry.date).getFullYear() === selectedYear
          )
          .reduce((sum, entry) => sum + entry.hours, 0);
        
        const remaining = budgetHours ? budgetHours - projectTotalHours : null;
        
        // Determine status based on project total vs budget
        let status = 'No Budget';
        if (budgetHours) {
          if (remaining < 0) {
            status = 'Over Budget';
          } else if (remaining < budgetHours * 0.2) {
            status = 'Near Limit';
          } else {
            status = 'On Track';
          }
        }
        
        exportRows.push([
          item.clientName,
          item.projectName,
          item.userName,
          item.totalHours.toFixed(1),
          budgetHours ? budgetHours.toString() : 'No Budget',
          remaining !== null ? remaining.toFixed(1) : '-',
          status
        ]);
      });
      
      // Sort rows by Client, then Project, then User
      exportRows.slice(1).sort((a, b) => {
        if (a[0] !== b[0]) return a[0].localeCompare(b[0]); // Client
        if (a[1] !== b[1]) return a[1].localeCompare(b[1]); // Project  
        return a[2].localeCompare(b[2]); // User
      });
      
      // Calculate totals
      const totalActual = Object.values(groupedData).reduce((sum, item) => sum + item.totalHours, 0);
      
      const totalBudget = monthlyBudgets.reduce((sum, budget) => {
        if (budget.year === selectedYear && budget.month === selectedMonth) {
          return sum + budget.budget_hours;
        }
        return sum;
      }, 0);
      
      const totalRemaining = totalBudget > 0 ? totalBudget - totalActual : null;
      
      // Add totals row
      exportRows.push([
        'TOTALS',
        '',
        '',
        totalActual.toFixed(1),
        totalBudget > 0 ? totalBudget.toString() : 'No Budget',
        totalRemaining !== null ? totalRemaining.toFixed(1) : '-',
        totalRemaining !== null && totalRemaining < 0 ? 'Over Budget' : 'Within Budget'
      ]);
      
      setExportData({
        month: `${monthNames[selectedMonth - 1]} ${selectedYear}`,
        data: exportRows,
        summary: {
          total_entries: Object.keys(groupedData).length,
          total_hours: totalActual,
          total_budget: totalBudget,
          projects_with_budget: monthlyBudgets.filter(b => b.year === selectedYear && b.month === selectedMonth).length
        }
      });
    } catch (err) {
      console.error("Error generating export data:", err);
      alert("Error generating export data. Please try again.");
    }
  };

  const copyToClipboard = async () => {
    if (!exportData) return;
    
    // Convert to tab-separated format for Excel
    const tsvData = exportData.data
      .map(row => row.join('\t'))
      .join('\n');
    
    try {
      await navigator.clipboard.writeText(tsvData);
      alert("Data copied to clipboard! You can now paste it directly into Excel.");
    } catch (err) {
      console.error("Error copying to clipboard:", err);
      alert("Could not copy to clipboard. Please select and copy the data manually.");
    }
  };

  const fetchFinalizedTimesheets = async () => {
    try {
      const response = await apiClient.get("/admin/finalized-timesheets");
      setFinalizedTimesheets(response.data);
    } catch (err) {
      console.error("Error fetching finalized timesheets:", err);
      setFinalizedTimesheets([]);
    }
  };

  const unlockTimesheet = async (userId, year, month, userName) => {
    const monthName = monthNames[month - 1];
    
    if (!window.confirm(`🔓 UNLOCK TIMESHEET\n\nThis will unlock ${userName}'s ${monthName} ${year} timesheet, allowing them to make changes.\n\nThey will need to finalize it again after making changes.\n\nProceed?`)) {
      return;
    }

    try {
      const response = await apiClient.post(`/admin/unlock-monthly-hours/${userId}/${year}/${month}`);
      
      if (response.data) {
        alert(`✅ Timesheet Unlocked Successfully!\n\n` +
              `User: ${response.data.user_name}\n` +
              `Month: ${response.data.month} ${response.data.year}\n` +
              `Time entries unlocked: ${response.data.time_entries_unlocked}\n\n` +
              `${response.data.user_name} can now edit their timesheet and will need to finalize it again.`);
              
        // Refresh the finalized timesheets list
        fetchFinalizedTimesheets();
      }
    } catch (err) {
      console.error("Error unlocking timesheet:", err);
      const errorMsg = err.response?.data?.detail || "Error unlocking timesheet. Please try again.";
      alert(`Unlock failed: ${errorMsg}`);
    }
  };

  // CRUD functions for clients
  const updateClient = async () => {
    try {
      await apiClient.put(`/clients/${editingClient.id}`, {
        name: editingClient.name,
        contact_info: editingClient.contact_info
      });
      setEditingClient(null);
      fetchData();
    } catch (err) {
      console.error("Error updating client:", err);
      alert("Error updating client. Please try again.");
    }
  };

  const deleteClient = async (clientId, clientName) => {
    if (!window.confirm(`Are you sure you want to delete "${clientName}"? This action cannot be undone.`)) {
      return;
    }
    
    try {
      await apiClient.delete(`/clients/${clientId}`);
      fetchData();
    } catch (err) {
      console.error("Error deleting client:", err);
      const errorMsg = err.response?.data?.detail || "Error deleting client. Please try again.";
      alert(errorMsg);
    }
  };

  // CRUD functions for projects
  const updateProject = async () => {
    try {
      const projectData = {
        name: editingProject.name,
        client_id: editingProject.client_id,
        assigned_user_ids: editingProject.assigned_user_ids || []
      };
      await apiClient.put(`/projects/${editingProject.id}`, projectData);
      setEditingProject(null);
      fetchData();
    } catch (err) {
      console.error("Error updating project:", err);
      alert("Error updating project. Please try again.");
    }
  };

  const deleteProject = async (projectId, projectName) => {
    if (!window.confirm(`Are you sure you want to delete "${projectName}"? This action cannot be undone.`)) {
      return;
    }
    
    try {
      await apiClient.delete(`/projects/${projectId}`);
      fetchData();
    } catch (err) {
      console.error("Error deleting project:", err);
      const errorMsg = err.response?.data?.detail || "Error deleting project. Please try again.";
      alert(errorMsg);
    }
  };

  // CRUD functions for users
  const updateUser = async () => {
    try {
      const userData = {
        name: editingUser.name,
        email: editingUser.email,
        role: editingUser.role
      };
      if (editingUser.password) {
        userData.password = editingUser.password;
      }
      await apiClient.put(`/users/${editingUser.id}`, userData);
      setEditingUser(null);
      fetchData();
    } catch (err) {
      console.error("Error updating user:", err);
      const errorMsg = err.response?.data?.detail || "Error updating user. Please try again.";
      alert(errorMsg);
    }
  };

  const deleteUser = async (userId, userName) => {
    // Count user's time entries to inform admin
    const userEntries = timeEntries.filter(entry => entry.user_id === userId);
    const entriesText = userEntries.length > 0 
      ? `\n\nThis will also delete ${userEntries.length} time entries associated with this user.`
      : '';
    
    if (!window.confirm(`Are you sure you want to delete "${userName}"?${entriesText}\n\nThis action cannot be undone and will remove all associated data.`)) {
      return;
    }
    
    try {
      const response = await apiClient.delete(`/users/${userId}`);
      
      // Show success message with details
      if (response.data?.details) {
        const details = response.data.details;
        alert(`User "${details.user_name}" deleted successfully!\n\n` +
              `• Time entries deleted: ${details.time_entries_deleted}\n` +
              `• Monthly submissions deleted: ${details.monthly_submissions_deleted}\n` +
              `• Projects updated: ${details.projects_updated}`);
      }
      
      fetchData();
    } catch (err) {
      console.error("Error deleting user:", err);
      const errorMsg = err.response?.data?.detail || "Error deleting user. Please try again.";
      alert(errorMsg);
    }
  };

  // Sorting and filtering functions
  const sortProjects = (field) => {
    const direction = sortField === field && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortDirection(direction);
  };

  const filteredProjects = projects
    .filter(project => {
      return (!clientFilter || project.client_name.toLowerCase().includes(clientFilter.toLowerCase())) &&
             (!projectFilter || project.name.toLowerCase().includes(projectFilter.toLowerCase()));
    })
    .sort((a, b) => {
      if (!sortField) return 0;
      
      let aVal = a[sortField];
      let bVal = b[sortField];
      
      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }
      
      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="border-b bg-white">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold text-slate-800">Project Hours - Admin</h1>
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-600">Welcome, {user.name}</span>
            <Button variant="outline" onClick={onLogout} data-testid="logout-btn">
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6">
        {/* Global Month Navigation */}
        <Card className="mb-6">
          <CardContent className="py-4">
            <div className="flex items-center justify-center gap-4">
              <Button
                variant="outline"
                onClick={() => {
                  if (selectedMonth === 1) {
                    setSelectedMonth(12);
                    setSelectedYear(selectedYear - 1);
                  } else {
                    setSelectedMonth(selectedMonth - 1);
                  }
                }}
                data-testid="prev-month-admin"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <div className="text-lg font-semibold min-w-48 text-center">
                {monthNames[selectedMonth - 1]} {selectedYear}
              </div>
              <Button
                variant="outline"
                onClick={() => {
                  if (selectedMonth === 12) {
                    setSelectedMonth(1);
                    setSelectedYear(selectedYear + 1);
                  } else {
                    setSelectedMonth(selectedMonth + 1);
                  }
                }}
                data-testid="next-month-admin"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>

        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="grid w-full grid-cols-7">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="clients">Clients</TabsTrigger>
            <TabsTrigger value="projects">Projects</TabsTrigger>
            <TabsTrigger value="budgets">Manage Budgets</TabsTrigger>
            <TabsTrigger value="users">Users</TabsTrigger>
            <TabsTrigger value="timesheets">Finalized Timesheets</TabsTrigger>
            <TabsTrigger value="reports">Reports</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Active Clients</CardTitle>
                  <Users className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold" data-testid="total-clients">
                    {clients.filter(client => 
                      timeEntries.some(entry => 
                        projects.some(p => p.client_id === client.id && p.id === entry.project_id) &&
                        new Date(entry.date).getMonth() + 1 === selectedMonth &&
                        new Date(entry.date).getFullYear() === selectedYear
                      )
                    ).length}
                  </div>
                  <div className="text-xs text-muted-foreground">with hours in {monthNames[selectedMonth - 1]}</div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Active Projects</CardTitle>
                  <FolderOpen className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold" data-testid="total-projects">
                    {projects.filter(project => 
                      timeEntries.some(entry => 
                        entry.project_id === project.id &&
                        new Date(entry.date).getMonth() + 1 === selectedMonth &&
                        new Date(entry.date).getFullYear() === selectedYear
                      )
                    ).length}
                  </div>
                  <div className="text-xs text-muted-foreground">with hours in {monthNames[selectedMonth - 1]}</div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Hours</CardTitle>
                  <Clock className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold" data-testid="total-hours">
                    {timeEntries
                      .filter(entry => 
                        new Date(entry.date).getMonth() + 1 === selectedMonth &&
                        new Date(entry.date).getFullYear() === selectedYear
                      )
                      .reduce((sum, entry) => sum + entry.hours, 0)
                      .toFixed(1)}h
                  </div>
                  <div className="text-xs text-muted-foreground">{monthNames[selectedMonth - 1]} {selectedYear}</div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Team Members</CardTitle>
                  <BarChart3 className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-blue-600" data-testid="active-users">
                    {users.filter(user => 
                      user.role === 'team_member' &&
                      timeEntries.some(entry => 
                        entry.user_id === user.id &&
                        new Date(entry.date).getMonth() + 1 === selectedMonth &&
                        new Date(entry.date).getFullYear() === selectedYear
                      )
                    ).length}
                  </div>
                  <div className="text-xs text-muted-foreground">submitted hours</div>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Project Budget Status</CardTitle>
                <CardDescription>Current month budget usage for all projects</CardDescription>
              </CardHeader>
              <CardContent>
                {/* Filter Controls */}
                <div className="mb-4 flex gap-4">
                  <div className="flex-1">
                    <Input
                      placeholder="Filter by client..."
                      value={clientFilter}
                      onChange={(e) => setClientFilter(e.target.value)}
                      className="max-w-sm"
                    />
                  </div>
                  <div className="flex-1">
                    <Input
                      placeholder="Filter by project..."
                      value={projectFilter}
                      onChange={(e) => setProjectFilter(e.target.value)}
                      className="max-w-sm"
                    />
                  </div>
                </div>
                
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse border border-gray-300">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="border border-gray-300 p-3 text-left font-semibold cursor-pointer hover:bg-gray-100" onClick={() => sortProjects('client_name')}>
                          Client {sortField === 'client_name' && (sortDirection === 'asc' ? '↑' : '↓')}
                        </th>
                        <th className="border border-gray-300 p-3 text-left font-semibold cursor-pointer hover:bg-gray-100" onClick={() => sortProjects('name')}>
                          Project Name {sortField === 'name' && (sortDirection === 'asc' ? '↑' : '↓')}
                        </th>
                        <th className="border border-gray-300 p-3 text-center font-semibold cursor-pointer hover:bg-gray-100" onClick={() => sortProjects('current_month_hours')}>
                          Hours Used {sortField === 'current_month_hours' && (sortDirection === 'asc' ? '↑' : '↓')}
                        </th>
                        <th className="border border-gray-300 p-3 text-center font-semibold">Monthly Budget ({monthNames[selectedMonth - 1]})</th>
                        <th className="border border-gray-300 p-3 text-center font-semibold">Budget vs Actual</th>
                        <th className="border border-gray-300 p-3 text-center font-semibold">Users Submitted</th>
                        <th className="border border-gray-300 p-3 text-center font-semibold">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredProjects.map((project) => {
                        const assignedUsers = users.filter(u => project.assigned_user_ids.includes(u.id));
                        const monthlyHours = timeEntries
                          .filter(entry => 
                            entry.project_id === project.id &&
                            new Date(entry.date).getMonth() + 1 === selectedMonth &&
                            new Date(entry.date).getFullYear() === selectedYear
                          )
                          .reduce((sum, entry) => sum + entry.hours, 0);
                        const usersWithHours = assignedUsers.filter(user => {
                          return timeEntries.some(entry => 
                            entry.user_id === user.id && 
                            entry.project_id === project.id &&
                            new Date(entry.date).getMonth() + 1 === selectedMonth &&
                            new Date(entry.date).getFullYear() === selectedYear
                          );
                        });
                        // Get monthly budget for this project and selected month
                        const monthlyBudget = monthlyBudgets.find(b => 
                          b.project_id === project.id && 
                          b.year === selectedYear && 
                          b.month === selectedMonth
                        );
                        const budgetHours = monthlyBudget ? monthlyBudget.budget_hours : null;
                        const remaining = budgetHours ? budgetHours - monthlyHours : null;
                        
                        return (
                          <tr key={project.id} className="hover:bg-gray-50">
                            <td className="border border-gray-300 p-3 font-medium">{project.client_name}</td>
                            <td className="border border-gray-300 p-3 text-sm">{project.name}</td>
                            <td className="border border-gray-300 p-3 text-center">
                              <span className={remaining !== null && remaining < 0 ? "text-red-600 font-bold" : "font-medium"}>
                                {monthlyHours}h
                              </span>
                            </td>
                            <td className="border border-gray-300 p-3 text-center">
                              {budgetHours ? `${budgetHours}h` : 'No budget set'}
                            </td>
                            <td className="border border-gray-300 p-3 text-center">
                              {budgetHours ? (
                                <span className={remaining < 0 ? "text-red-600 font-bold" : remaining < budgetHours * 0.2 ? "text-orange-600 font-medium" : "text-green-600 font-medium"}>
                                  {remaining > 0 ? `+${remaining}h` : `${remaining}h`}
                                </span>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </td>
                            <td className="border border-gray-300 p-3 text-center">
                              <span className="text-sm">
                                {usersWithHours.length}/{assignedUsers.length}
                              </span>
                            </td>
                            <td className="border border-gray-300 p-3 text-center">
                              {budgetHours ? (
                                remaining < 0 ? (
                                  <Badge variant="destructive">Over Budget</Badge>
                                ) : remaining < budgetHours * 0.2 ? (
                                  <Badge variant="outline" className="text-orange-600">Near Limit</Badge>
                                ) : (
                                  <Badge variant="outline" className="text-green-600">On Track</Badge>
                                )
                              ) : (
                                <Badge variant="secondary">No Budget</Badge>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                      {projects.length === 0 && (
                        <tr>
                          <td colSpan={7} className="border border-gray-300 p-6 text-center text-gray-500">
                            No projects found. Create some projects to see budget tracking.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Quick Setup</CardTitle>
                <CardDescription>Initialize sample data to get started</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Button onClick={initSampleData} data-testid="init-sample-data-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Create Sample Data
                  </Button>
                </div>
                <div>
                  <Button 
                    onClick={cleanupSampleData} 
                    variant="destructive" 
                    data-testid="cleanup-sample-data-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete ALL Data (Keep Admin Only)
                  </Button>
                </div>
                <div>
                  <Button 
                    onClick={productionReset} 
                    variant="destructive"
                    className="bg-red-800 hover:bg-red-900 text-white border-red-800"
                    data-testid="production-reset-btn"
                  >
                    <AlertTriangle className="w-4 h-4 mr-2" />
                    🚨 PRODUCTION RESET 🚨
                  </Button>
                </div>
                <div>
                  <Button 
                    onClick={validateDeployment} 
                    variant="outline"
                    data-testid="validate-deployment-btn"
                  >
                    <BarChart3 className="w-4 h-4 mr-2" />
                    Validate Deployment
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Data Management</CardTitle>
                <CardDescription>Backup and restore your data across deployments</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Button onClick={createBackup} variant="default">
                    <Plus className="w-4 h-4 mr-2" />
                    Create Backup
                  </Button>
                </div>
                <div>
                  <Button onClick={exportAllData} variant="outline">
                    <BarChart3 className="w-4 h-4 mr-2" />
                    Export Data (Download)
                  </Button>
                </div>
                <div>
                  <Button onClick={listBackups} variant="outline">
                    <Clock className="w-4 h-4 mr-2" />
                    View Backups
                  </Button>
                </div>
                <div>
                  <Button onClick={restoreBackup} variant="secondary">
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Restore Backup
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="clients" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Add New Client</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="client-name">Client Name</Label>
                  <Input
                    id="client-name"
                    placeholder="Company Name"
                    value={newClient.name}
                    onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
                    data-testid="new-client-name-input"
                  />
                </div>
                <Button onClick={createClient} data-testid="create-client-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Client
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Clients</CardTitle>
                <CardDescription>Manage your client list</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse border border-gray-300">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="border border-gray-300 p-3 text-left font-semibold">Client Name</th>
                        <th className="border border-gray-300 p-3 text-center font-semibold">Projects</th>
                        <th className="border border-gray-300 p-3 text-center font-semibold">Created</th>
                        <th className="border border-gray-300 p-3 text-center font-semibold">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {clients.map((client) => {
                        const clientProjects = projects.filter(p => p.client_id === client.id);
                        
                        return (
                          <tr key={client.id} className="hover:bg-gray-50">
                            <td className="border border-gray-300 p-3 font-medium">{client.name}</td>
                            <td className="border border-gray-300 p-3 text-center">
                              <Badge variant="outline">
                                {clientProjects.length} projects
                              </Badge>
                            </td>
                            <td className="border border-gray-300 p-3 text-center text-sm text-gray-500">
                              {new Date(client.created_at).toLocaleDateString()}
                            </td>
                            <td className="border border-gray-300 p-3 text-center">
                              <div className="flex gap-2 justify-center">
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  onClick={() => setEditingClient(client)}
                                  data-testid={`edit-client-${client.id}`}
                                >
                                  <Edit className="w-3 h-3" />
                                </Button>
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  onClick={() => deleteClient(client.id, client.name)}
                                  data-testid={`delete-client-${client.id}`}
                                  className="text-red-600 hover:text-red-700"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Edit Client Dialog */}
            {editingClient && (
              <Dialog open={!!editingClient} onOpenChange={() => setEditingClient(null)}>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Edit Client</DialogTitle>
                    <DialogDescription>Update client information</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="edit-client-name">Client Name</Label>
                      <Input
                        id="edit-client-name"
                        value={editingClient.name}
                        onChange={(e) => setEditingClient({ ...editingClient, name: e.target.value })}
                        data-testid="edit-client-name-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="edit-client-contact">Contact Info</Label>
                      <Input
                        id="edit-client-contact"
                        value={editingClient.contact_info || ''}
                        onChange={(e) => setEditingClient({ ...editingClient, contact_info: e.target.value })}
                        data-testid="edit-client-contact-input"
                      />
                    </div>
                    <div className="flex gap-2 justify-end">
                      <Button variant="outline" onClick={() => setEditingClient(null)}>
                        Cancel
                      </Button>
                      <Button onClick={updateClient} data-testid="update-client-btn">
                        Update Client
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </TabsContent>

          <TabsContent value="projects" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Add New Project</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="project-name">Project Name</Label>
                    <Input
                      id="project-name"
                      placeholder="Project Name"
                      value={newProject.name}
                      onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                      data-testid="new-project-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="project-client">Client</Label>
                    <Select 
                      value={newProject.client_id} 
                      onValueChange={(value) => setNewProject({ ...newProject, client_id: value })}
                    >
                      <SelectTrigger data-testid="new-project-client-select">
                        <SelectValue placeholder="Select client" />
                      </SelectTrigger>
                      <SelectContent>
                        {clients.map((client) => (
                          <SelectItem key={client.id} value={client.id}>
                            {client.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Button onClick={createProject} data-testid="create-project-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Project
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Projects</CardTitle>
                <CardDescription>Manage your projects and team assignments</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse border border-gray-300">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="border border-gray-300 p-3 text-left font-semibold">Project Name</th>
                        <th className="border border-gray-300 p-3 text-left font-semibold">Client</th>
                        <th className="border border-gray-300 p-3 text-center font-semibold">Assigned Team</th>
                        <th className="border border-gray-300 p-3 text-center font-semibold">Project Managers</th>
                        <th className="border border-gray-300 p-3 text-center font-semibold">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {projects.map((project) => (
                        <tr key={project.id} className="hover:bg-gray-50">
                          <td className="border border-gray-300 p-3 font-medium">{project.name}</td>
                          <td className="border border-gray-300 p-3 text-sm">{project.client_name}</td>
                          <td className="border border-gray-300 p-3">
                            <div className="flex flex-wrap gap-1 justify-center">
                              {project.assigned_user_ids.length > 0 ? (
                                project.assigned_user_ids.map((userId) => {
                                  const user = users.find(u => u.id === userId);
                                  return user ? (
                                    <Badge key={userId} variant="secondary" className="text-xs">
                                      {user.name}
                                    </Badge>
                                  ) : null;
                                })
                              ) : (
                                <span className="text-xs text-gray-400">No assignments</span>
                              )}
                            </div>
                          </td>
                          <td className="border border-gray-300 p-3">
                            <div className="flex flex-wrap gap-1 justify-center">
                              {project.project_manager_ids && project.project_manager_ids.length > 0 ? (
                                project.project_manager_ids.map((pmId) => {
                                  const pm = users.find(u => u.id === pmId);
                                  return pm ? (
                                    <Badge key={pmId} variant="outline" className="text-xs border-blue-500 text-blue-700">
                                      {pm.name}
                                    </Badge>
                                  ) : null;
                                })
                              ) : (
                                <span className="text-xs text-gray-400">No managers</span>
                              )}
                            </div>
                          </td>
                          <td className="border border-gray-300 p-3 text-center">
                            <div className="flex gap-2 justify-center">
                              <Dialog>
                                <DialogTrigger asChild>
                                  <Button variant="outline" size="sm" data-testid={`assign-users-${project.id}`}>
                                    <Users className="w-3 h-3" />
                                  </Button>
                                </DialogTrigger>
                                <DialogContent>
                                  <DialogHeader>
                                    <DialogTitle>Assign Team Members</DialogTitle>
                                    <DialogDescription>
                                      Select team members to assign to {project.name}
                                    </DialogDescription>
                                  </DialogHeader>
                                  <div className="space-y-4">
                                    {users.filter(u => u.role === 'team_member').map((user) => (
                                      <div key={user.id} className="flex items-center space-x-2">
                                        <input
                                          type="checkbox"
                                          id={`user-${user.id}-${project.id}`}
                                          checked={project.assigned_user_ids.includes(user.id)}
                                          onChange={async (e) => {
                                            const isAssigned = e.target.checked;
                                            const updatedUserIds = isAssigned 
                                              ? [...project.assigned_user_ids, user.id]
                                              : project.assigned_user_ids.filter(id => id !== user.id);
                                            
                                            try {
                                              await apiClient.put(`/projects/${project.id}/assign`, {
                                                assigned_user_ids: updatedUserIds
                                              });
                                              fetchData(); // Refresh data
                                            } catch (err) {
                                              console.error("Error updating project assignment:", err);
                                            }
                                          }}
                                          data-testid={`assign-checkbox-${user.id}-${project.id}`}
                                        />
                                        <label htmlFor={`user-${user.id}-${project.id}`} className="text-sm">
                                          {user.name} ({user.email})
                                        </label>
                                      </div>
                                    ))}
                                  </div>
                                </DialogContent>
                              </Dialog>
                              <Dialog>
                                <DialogTrigger asChild>
                                  <Button variant="outline" size="sm" className="text-blue-600 hover:text-blue-700" data-testid={`assign-managers-${project.id}`}>
                                    <FolderOpen className="w-3 h-3" />
                                  </Button>
                                </DialogTrigger>
                                <DialogContent>
                                  <DialogHeader>
                                    <DialogTitle>Assign Project Managers</DialogTitle>
                                    <DialogDescription>
                                      Select project managers for {project.name}. They will automatically be assigned as team members and can log time entries.
                                    </DialogDescription>
                                  </DialogHeader>
                                  <div className="space-y-4">
                                    {users.filter(u => u.role === 'project_manager').map((manager) => (
                                      <div key={manager.id} className="flex items-center space-x-2">
                                        <input
                                          type="checkbox"
                                          id={`manager-${manager.id}-${project.id}`}
                                          checked={project.project_manager_ids && project.project_manager_ids.includes(manager.id)}
                                          onChange={async (e) => {
                                            const isAssigned = e.target.checked;
                                            const currentManagers = project.project_manager_ids || [];
                                            const updatedManagerIds = isAssigned 
                                              ? [...currentManagers, manager.id]
                                              : currentManagers.filter(id => id !== manager.id);
                                            
                                            try {
                                              await assignProjectManagers(project.id, updatedManagerIds);
                                            } catch (err) {
                                              console.error("Error updating project manager assignment:", err);
                                            }
                                          }}
                                          data-testid={`assign-manager-checkbox-${manager.id}-${project.id}`}
                                        />
                                        <label htmlFor={`manager-${manager.id}-${project.id}`} className="text-sm">
                                          {manager.name} ({manager.email})
                                        </label>
                                      </div>
                                    ))}
                                    {users.filter(u => u.role === 'project_manager').length === 0 && (
                                      <p className="text-sm text-gray-500">No project managers available. Create users with 'Project Manager' role first.</p>
                                    )}
                                  </div>
                                </DialogContent>
                              </Dialog>
                              <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={() => setEditingProject({...project})}
                                data-testid={`edit-project-${project.id}`}
                              >
                                <Edit className="w-3 h-3" />
                              </Button>
                              <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={() => deleteProject(project.id, project.name)}
                                data-testid={`delete-project-${project.id}`}
                                className="text-red-600 hover:text-red-700"
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Edit Project Dialog */}
            {editingProject && (
              <Dialog open={!!editingProject} onOpenChange={() => setEditingProject(null)}>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Edit Project</DialogTitle>
                    <DialogDescription>Update project information</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="edit-project-name">Project Name</Label>
                      <Input
                        id="edit-project-name"
                        value={editingProject.name}
                        onChange={(e) => setEditingProject({ ...editingProject, name: e.target.value })}
                        data-testid="edit-project-name-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="edit-project-client">Client</Label>
                      <Select 
                        value={editingProject.client_id} 
                        onValueChange={(value) => setEditingProject({ ...editingProject, client_id: value })}
                      >
                        <SelectTrigger data-testid="edit-project-client-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {clients.map((client) => (
                            <SelectItem key={client.id} value={client.id}>
                              {client.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex gap-2 justify-end">
                      <Button variant="outline" onClick={() => setEditingProject(null)}>
                        Cancel
                      </Button>
                      <Button onClick={updateProject} data-testid="update-project-btn">
                        Update Project
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </TabsContent>

          <TabsContent value="budgets" className="space-y-6">
            <ManageBudgets 
              selectedMonth={selectedMonth} 
              selectedYear={selectedYear} 
              monthNames={monthNames} 
            />
          </TabsContent>

          <TabsContent value="users" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Add New User</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="user-name">Name</Label>
                    <Input
                      id="user-name"
                      placeholder="John Doe"
                      value={newUser.name}
                      onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                      data-testid="new-user-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="user-email">Email</Label>
                    <Input
                      id="user-email"
                      type="email"
                      placeholder="john@company.com"
                      value={newUser.email}
                      onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                      data-testid="new-user-email-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="user-role">Role</Label>
                    <Select 
                      value={newUser.role} 
                      onValueChange={(value) => setNewUser({ ...newUser, role: value })}
                    >
                      <SelectTrigger data-testid="new-user-role-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="team_member">Team Member</SelectItem>
                        <SelectItem value="project_manager">Project Manager</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="user-password">Password</Label>
                    <Input
                      id="user-password"
                      type="password"
                      placeholder="Password"
                      value={newUser.password}
                      onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                      data-testid="new-user-password-input"
                    />
                  </div>
                </div>
                <Button onClick={createUser} data-testid="create-user-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add User
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Users</CardTitle>
                <CardDescription>Manage team members and admins</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse border border-gray-300">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="border border-gray-300 p-3 text-left font-semibold">Name</th>
                        <th className="border border-gray-300 p-3 text-left font-semibold">Email</th>
                        <th className="border border-gray-300 p-3 text-center font-semibold">Role</th>
                        <th className="border border-gray-300 p-3 text-center font-semibold">Hours This Month</th>
                        <th className="border border-gray-300 p-3 text-center font-semibold">Projects Assigned</th>
                        <th className="border border-gray-300 p-3 text-center font-semibold">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((user) => {
                        const userHours = timeEntries
                          .filter(entry => entry.user_id === user.id)
                          .filter(entry => 
                            new Date(entry.date).getMonth() + 1 === selectedMonth &&
                            new Date(entry.date).getFullYear() === selectedYear
                          )
                          .reduce((sum, entry) => sum + entry.hours, 0);
                        
                        return (
                          <tr key={user.id} className="hover:bg-gray-50">
                            <td className="border border-gray-300 p-3 font-medium">{user.name}</td>
                            <td className="border border-gray-300 p-3 text-sm text-gray-600">{user.email}</td>
                            <td className="border border-gray-300 p-3 text-center">
                              <Badge variant={user.role === 'admin' ? 'default' : 'secondary'}>
                                {user.role === 'admin' ? 'Admin' : 'Team Member'}
                              </Badge>
                            </td>
                            <td className="border border-gray-300 p-3 text-center font-medium">
                              {user.role === 'team_member' ? `${userHours}h` : 'N/A'}
                            </td>
                            <td className="border border-gray-300 p-3 text-center">
                              {user.role === 'team_member' ? (
                                <div className="flex flex-wrap gap-1 justify-center">
                                  {projects.filter(p => p.assigned_user_ids.includes(user.id)).map(project => (
                                    <Badge key={project.id} variant="outline" className="text-xs">
                                      {project.name}
                                    </Badge>
                                  ))}
                                  {projects.filter(p => p.assigned_user_ids.includes(user.id)).length === 0 && (
                                    <span className="text-xs text-gray-400">No projects</span>
                                  )}
                                </div>
                              ) : (
                                <span className="text-sm text-gray-400">N/A (Admin)</span>
                              )}
                            </td>
                            <td className="border border-gray-300 p-3 text-center">
                              <div className="flex gap-1 justify-center">
                                {user.role === 'team_member' && (
                                  <Button 
                                    variant="outline" 
                                    size="sm" 
                                    onClick={() => setViewingUserHours(user)}
                                    data-testid={`view-hours-${user.id}`}
                                    title="View/Adjust Hours"
                                  >
                                    <Clock className="w-3 h-3" />
                                  </Button>
                                )}
                                {user.role === 'team_member' && (
                                  <Button 
                                    variant="outline" 
                                    size="sm" 
                                    onClick={() => setAssigningUser(user)}
                                    data-testid={`assign-projects-${user.id}`}
                                    title="Assign Projects"
                                  >
                                    <Users className="w-3 h-3" />
                                  </Button>
                                )}
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  onClick={() => setEditingUser({...user, password: ''})}
                                  data-testid={`edit-user-${user.id}`}
                                  title="Edit User"
                                >
                                  <Edit className="w-3 h-3" />
                                </Button>
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  onClick={() => deleteUser(user.id, user.name)}
                                  data-testid={`delete-user-${user.id}`}
                                  className="text-red-600 hover:text-red-700"
                                  title="Delete User"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Edit User Dialog */}
            {editingUser && (
              <Dialog open={!!editingUser} onOpenChange={() => setEditingUser(null)}>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Edit User</DialogTitle>
                    <DialogDescription>Update user information</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="edit-user-name">Name</Label>
                      <Input
                        id="edit-user-name"
                        value={editingUser.name}
                        onChange={(e) => setEditingUser({ ...editingUser, name: e.target.value })}
                        data-testid="edit-user-name-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="edit-user-email">Email</Label>
                      <Input
                        id="edit-user-email"
                        type="email"
                        value={editingUser.email}
                        onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
                        data-testid="edit-user-email-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="edit-user-role">Role</Label>
                      <Select 
                        value={editingUser.role} 
                        onValueChange={(value) => setEditingUser({ ...editingUser, role: value })}
                      >
                        <SelectTrigger data-testid="edit-user-role-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="team_member">Team Member</SelectItem>
                          <SelectItem value="project_manager">Project Manager</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="edit-user-password">New Password (leave blank to keep current)</Label>
                      <Input
                        id="edit-user-password"
                        type="password"
                        value={editingUser.password}
                        onChange={(e) => setEditingUser({ ...editingUser, password: e.target.value })}
                        data-testid="edit-user-password-input"
                        placeholder="Leave blank to keep current password"
                      />
                    </div>
                    <div className="flex gap-2 justify-end">
                      <Button variant="outline" onClick={() => setEditingUser(null)}>
                        Cancel
                      </Button>
                      <Button onClick={updateUser} data-testid="update-user-btn">
                        Update User
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            )}

            {/* View/Adjust User Hours Dialog */}
            {viewingUserHours && (
              <Dialog open={!!viewingUserHours} onOpenChange={() => setViewingUserHours(null)}>
                <DialogContent className="max-w-4xl">
                  <DialogHeader>
                    <DialogTitle>Hours for {viewingUserHours.name}</DialogTitle>
                    <DialogDescription>View and adjust submitted hours for this user</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 max-h-96 overflow-y-auto">
                    {timeEntries
                      .filter(entry => entry.user_id === viewingUserHours.id)
                      .filter(entry => new Date(entry.date).getMonth() === new Date().getMonth())
                      .map((entry) => (
                        <div key={entry.id} className="flex justify-between items-center p-3 border rounded">
                          <div>
                            <div className="font-medium">{entry.project_name}</div>
                            <div className="text-sm text-gray-600">{entry.date} - {entry.description}</div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              step="0.25"
                              value={entry.hours}
                              onChange={async (e) => {
                                try {
                                  await apiClient.put(`/time-entries/${entry.id}`, {
                                    project_id: entry.project_id,
                                    date: entry.date,
                                    hours: parseFloat(e.target.value) || 0,
                                    description: entry.description
                                  });
                                  fetchData();
                                } catch (err) {
                                  console.error("Error updating hours:", err);
                                }
                              }}
                              className="w-20 text-center"
                            />
                            <span className="text-sm">hours</span>
                          </div>
                        </div>
                      ))}
                    {timeEntries.filter(entry => 
                      entry.user_id === viewingUserHours.id && 
                      new Date(entry.date).getMonth() === new Date().getMonth()
                    ).length === 0 && (
                      <div className="text-center text-gray-500 py-6">
                        No hours submitted this month
                      </div>
                    )}
                  </div>
                </DialogContent>
              </Dialog>
            )}

            {/* Assign Projects to User Dialog */}
            {assigningUser && (
              <Dialog open={!!assigningUser} onOpenChange={() => setAssigningUser(null)}>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Assign Projects to {assigningUser.name}</DialogTitle>
                    <DialogDescription>Select projects to assign to this user</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 max-h-96 overflow-y-auto">
                    {projects.map((project) => (
                      <div key={project.id} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id={`assign-${project.id}-${assigningUser.id}`}
                          checked={project.assigned_user_ids.includes(assigningUser.id)}
                          onChange={async (e) => {
                            const isAssigned = e.target.checked;
                            const updatedUserIds = isAssigned 
                              ? [...project.assigned_user_ids, assigningUser.id]
                              : project.assigned_user_ids.filter(id => id !== assigningUser.id);
                            
                            try {
                              await apiClient.put(`/projects/${project.id}/assign`, {
                                assigned_user_ids: updatedUserIds
                              });
                              fetchData();
                            } catch (err) {
                              console.error("Error updating project assignment:", err);
                            }
                          }}
                        />
                        <label htmlFor={`assign-${project.id}-${assigningUser.id}`} className="flex-1">
                          <div className="font-medium">{project.name}</div>
                          <div className="text-sm text-gray-600">
                            {project.client_name}
                          </div>
                        </label>
                      </div>
                    ))}
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </TabsContent>

          <TabsContent value="timesheets" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Finalized Timesheets</CardTitle>
                <CardDescription>View and unlock finalized monthly timesheets for users</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="mb-4">
                  <Button 
                    onClick={fetchFinalizedTimesheets} 
                    variant="outline"
                    className="mb-4"
                  >
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Refresh
                  </Button>
                </div>
                
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse border border-gray-300">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="border border-gray-300 p-3 text-left font-semibold">User</th>
                        <th className="border border-gray-300 p-3 text-center font-semibold">Email</th>
                        <th className="border border-gray-300 p-3 text-center font-semibold">Month</th>
                        <th className="border border-gray-300 p-3 text-center font-semibold">Year</th>
                        <th className="border border-gray-300 p-3 text-center font-semibold">Total Hours</th>
                        <th className="border border-gray-300 p-3 text-center font-semibold">Entries</th>
                        <th className="border border-gray-300 p-3 text-center font-semibold">Finalized At</th>
                        <th className="border border-gray-300 p-3 text-center font-semibold">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {finalizedTimesheets.length > 0 ? (
                        finalizedTimesheets.map((timesheet) => (
                          <tr key={`${timesheet.user_id}-${timesheet.year}-${timesheet.month}`} className="hover:bg-gray-50">
                            <td className="border border-gray-300 p-3 font-medium">
                              {timesheet.user_name}
                            </td>
                            <td className="border border-gray-300 p-3 text-center text-sm text-gray-600">
                              {timesheet.user_email}
                            </td>
                            <td className="border border-gray-300 p-3 text-center">
                              <Badge variant="outline">
                                {timesheet.month_name}
                              </Badge>
                            </td>
                            <td className="border border-gray-300 p-3 text-center font-medium">
                              {timesheet.year}
                            </td>
                            <td className="border border-gray-300 p-3 text-center">
                              <span className="font-semibold text-blue-600">
                                {timesheet.total_hours.toFixed(1)}h
                              </span>
                            </td>
                            <td className="border border-gray-300 p-3 text-center">
                              <Badge variant="secondary">
                                {timesheet.entry_count} entries
                              </Badge>
                            </td>
                            <td className="border border-gray-300 p-3 text-center text-sm text-gray-500">
                              {timesheet.finalized_at ? new Date(timesheet.finalized_at).toLocaleDateString() : 'Unknown'}
                              {timesheet.finalized_at && (
                                <div className="text-xs">
                                  {new Date(timesheet.finalized_at).toLocaleTimeString()}
                                </div>
                              )}
                            </td>
                            <td className="border border-gray-300 p-3 text-center">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => unlockTimesheet(
                                  timesheet.user_id, 
                                  timesheet.year, 
                                  timesheet.month,
                                  timesheet.user_name
                                )}
                                data-testid={`unlock-timesheet-${timesheet.user_id}-${timesheet.year}-${timesheet.month}`}
                                className="text-orange-600 hover:text-orange-700 hover:border-orange-600"
                              >
                                <RotateCcw className="w-3 h-3 mr-1" />
                                Unlock
                              </Button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={8} className="border border-gray-300 p-6 text-center text-gray-500">
                            No finalized timesheets found. Users will appear here after they press "Send Final Hours".
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                
                {finalizedTimesheets.length > 0 && (
                  <div className="mt-4 text-sm text-gray-600 bg-blue-50 p-4 rounded-lg">
                    <div className="font-medium mb-2">📋 Information:</div>
                    <ul className="space-y-1 text-xs">
                      <li>• Finalized timesheets are locked and cannot be edited by users</li>
                      <li>• Click "Unlock" to allow a user to modify their timesheet</li>
                      <li>• After unlocking, the user will need to finalize their timesheet again</li>
                      <li>• All unlock actions are logged for audit purposes</li>
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="reports" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Monthly Export</CardTitle>
                <CardDescription>Export detailed monthly timesheet data by user for Excel</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="text-sm text-gray-600">
                    Exporting data for: <strong>{monthNames[selectedMonth - 1]} {selectedYear}</strong>
                  </div>
                  <Button onClick={exportMonthlyData} data-testid="export-monthly-btn">
                    Export {monthNames[selectedMonth - 1]} {selectedYear} to Excel
                  </Button>
                </div>
                
                {exportData && (
                  <div className="mt-6">
                    <h3 className="font-medium mb-2">Export Preview ({exportData.month})</h3>
                    <div className="bg-gray-50 p-4 rounded text-xs font-mono overflow-x-auto">
                      <table className="w-full border-collapse">
                        <tbody>
                          {exportData.data.map((row, index) => (
                            <tr key={index} className={index === 0 ? "font-bold" : ""}>
                              {row.map((cell, cellIndex) => (
                                <td key={cellIndex} className="border border-gray-300 px-2 py-1 text-center">
                                  {cell}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="mt-4 text-sm text-gray-600">
                      Summary: {exportData.summary.total_entries} user entries, {exportData.summary.total_hours}h actual, {exportData.summary.total_budget}h budgeted, {exportData.summary.projects_with_budget} projects with budgets
                    </div>
                    <Button 
                      onClick={copyToClipboard} 
                      variant="outline" 
                      className="mt-2"
                      data-testid="copy-export-btn"
                    >
                      Copy to Clipboard (Excel Ready)
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Overview Report</CardTitle>
                <CardDescription>Comprehensive overview of all clients, projects, and team member hours</CardDescription>
              </CardHeader>
              <CardContent>
                {/* Filter Controls */}
                <div className="mb-4 flex gap-4">
                  <Input
                    placeholder="Filter by client or project..."
                    className="max-w-sm"
                    onChange={(e) => setReportFilter(e.target.value)}
                  />
                </div>
                
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse border border-gray-300">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="border border-gray-300 p-3 text-left font-semibold cursor-pointer hover:bg-gray-100">
                          Client
                        </th>
                        <th className="border border-gray-300 p-3 text-left font-semibold cursor-pointer hover:bg-gray-100">
                          Project
                        </th>
                        <th className="border border-gray-300 p-3 text-center font-semibold cursor-pointer hover:bg-gray-100">
                          Team Members
                        </th>
                        <th className="border border-gray-300 p-3 text-center font-semibold cursor-pointer hover:bg-gray-100">
                          Total Hours
                        </th>
                        <th className="border border-gray-300 p-3 text-center font-semibold">
                          Monthly Budget ({monthNames[selectedMonth - 1]})
                        </th>
                        <th className="border border-gray-300 p-3 text-center font-semibold">
                          Budget vs Actual
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {projects
                        .filter(project => !reportFilter || 
                          project.name.toLowerCase().includes(reportFilter.toLowerCase()) ||
                          project.client_name.toLowerCase().includes(reportFilter.toLowerCase())
                        )
                        .map((project) => {
                          const projectHours = timeEntries
                            .filter(entry => 
                              entry.project_id === project.id &&
                              new Date(entry.date).getMonth() + 1 === selectedMonth &&
                              new Date(entry.date).getFullYear() === selectedYear
                            )
                            .reduce((sum, entry) => sum + entry.hours, 0);
                          
                          const assignedUsers = users.filter(u => project.assigned_user_ids.includes(u.id));
                          
                          // Get monthly budget for this project and selected month
                          const monthlyBudget = monthlyBudgets.find(b => 
                            b.project_id === project.id && 
                            b.year === selectedYear && 
                            b.month === selectedMonth
                          );
                          const budgetHours = monthlyBudget ? monthlyBudget.budget_hours : null;
                          const remaining = budgetHours ? budgetHours - projectHours : null;
                          
                          return (
                            <tr key={project.id} className="hover:bg-gray-50">
                              <td className="border border-gray-300 p-3 font-medium">{project.client_name}</td>
                              <td className="border border-gray-300 p-3">{project.name}</td>
                              <td className="border border-gray-300 p-3 text-center">
                                <div className="flex flex-wrap gap-1 justify-center">
                                  {assignedUsers.map(user => (
                                    <Badge key={user.id} variant="outline" className="text-xs">
                                      {user.name}
                                    </Badge>
                                  ))}
                                </div>
                              </td>
                              <td className="border border-gray-300 p-3 text-center font-bold">
                                <span className={remaining !== null && remaining < 0 ? "text-red-600" : ""}>
                                  {projectHours}h
                                </span>
                              </td>
                              <td className="border border-gray-300 p-3 text-center">
                                {budgetHours ? `${budgetHours}h` : 'No budget set'}
                              </td>
                              <td className="border border-gray-300 p-3 text-center">
                                {budgetHours ? (
                                  <span className={remaining < 0 ? "text-red-600 font-bold" : remaining < budgetHours * 0.2 ? "text-orange-600 font-medium" : "text-green-600 font-medium"}>
                                    {remaining > 0 ? `+${remaining}h` : `${remaining}h`}
                                  </span>
                                ) : (
                                  <span className="text-gray-400">-</span>
                                )}
                              </td>
                            </tr>
                          );
                        })
                      }
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Time Entry Details</CardTitle>
                <CardDescription>Detailed time entries with billable status controls for {monthNames[selectedMonth - 1]} {selectedYear}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse border border-gray-300">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="border border-gray-300 p-3 text-left font-semibold">Date</th>
                        <th className="border border-gray-300 p-3 text-left font-semibold">User</th>
                        <th className="border border-gray-300 p-3 text-left font-semibold">Client</th>
                        <th className="border border-gray-300 p-3 text-left font-semibold">Project</th>
                        <th className="border border-gray-300 p-3 text-center font-semibold">Hours</th>
                        <th className="border border-gray-300 p-3 text-left font-semibold">Description</th>
                        <th className="border border-gray-300 p-3 text-center font-semibold">Billable</th>
                        <th className="border border-gray-300 p-3 text-center font-semibold">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {timeEntries
                        .filter(entry => 
                          new Date(entry.date).getMonth() + 1 === selectedMonth &&
                          new Date(entry.date).getFullYear() === selectedYear
                        )
                        .filter(entry => !reportFilter || 
                          projects.some(p => 
                            p.id === entry.project_id && 
                            (p.name.toLowerCase().includes(reportFilter.toLowerCase()) || 
                             p.client_name.toLowerCase().includes(reportFilter.toLowerCase()))
                          ) ||
                          users.some(u => 
                            u.id === entry.user_id && 
                            u.name.toLowerCase().includes(reportFilter.toLowerCase())
                          )
                        )
                        .sort((a, b) => new Date(b.date) - new Date(a.date))
                        .map((entry) => {
                          const project = projects.find(p => p.id === entry.project_id);
                          const user = users.find(u => u.id === entry.user_id);
                          
                          if (!project || !user) return null;
                          
                          return (
                            <tr key={entry.id} className="hover:bg-gray-50">
                              <td className="border border-gray-300 p-3 text-sm">
                                {new Date(entry.date).toLocaleDateString('en-US', { 
                                  weekday: 'short', 
                                  month: 'short', 
                                  day: 'numeric' 
                                })}
                              </td>
                              <td className="border border-gray-300 p-3 text-sm font-medium">{user.name}</td>
                              <td className="border border-gray-300 p-3 text-sm">{project.client_name}</td>
                              <td className="border border-gray-300 p-3 text-sm">{project.name}</td>
                              <td className="border border-gray-300 p-3 text-center font-bold">{entry.hours}h</td>
                              <td className="border border-gray-300 p-3 text-sm max-w-xs truncate" title={entry.description}>
                                {entry.description}
                              </td>
                              <td className="border border-gray-300 p-3 text-center">
                                <Badge variant={entry.is_billable ? "default" : "secondary"}>
                                  {entry.is_billable ? "Billable" : "Non-billable"}
                                </Badge>
                              </td>
                              <td className="border border-gray-300 p-3 text-center">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={async () => {
                                    const newBillableStatus = !entry.is_billable;
                                    const adminNote = prompt(
                                      `Mark this entry as ${newBillableStatus ? 'billable' : 'non-billable'}?\n\nOptional admin note:`,
                                      `Status changed to ${newBillableStatus ? 'billable' : 'non-billable'} by admin`
                                    );
                                    
                                    if (adminNote !== null) {
                                      try {
                                        await apiClient.post('/admin/override-billable', {
                                          entry_id: entry.id,
                                          is_billable: newBillableStatus,
                                          admin_note: adminNote
                                        });
                                        fetchData();
                                      } catch (err) {
                                        console.error("Error updating billable status:", err);
                                        alert("Error updating billable status. Please try again.");
                                      }
                                    }
                                  }}
                                  className={entry.is_billable ? "text-orange-600 hover:text-orange-700" : "text-green-600 hover:text-green-700"}
                                  data-testid={`toggle-billable-${entry.id}`}
                                >
                                  {entry.is_billable ? "Mark Non-billable" : "Mark Billable"}
                                </Button>
                              </td>
                            </tr>
                          );
                        })
                        .filter(Boolean)
                      }
                      {timeEntries.filter(entry => 
                        new Date(entry.date).getMonth() + 1 === selectedMonth &&
                        new Date(entry.date).getFullYear() === selectedYear
                      ).length === 0 && (
                        <tr>
                          <td colSpan={8} className="border border-gray-300 p-6 text-center text-gray-500">
                            No time entries found for {monthNames[selectedMonth - 1]} {selectedYear}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                
                {/* Billable Summary */}
                <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card>
                    <CardContent className="pt-6">
                      <div className="text-2xl font-bold text-green-600">
                        {timeEntries
                          .filter(entry => 
                            new Date(entry.date).getMonth() + 1 === selectedMonth &&
                            new Date(entry.date).getFullYear() === selectedYear &&
                            entry.is_billable
                          )
                          .reduce((sum, entry) => sum + entry.hours, 0)
                          .toFixed(1)}h
                      </div>
                      <p className="text-xs text-muted-foreground">Billable Hours</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <div className="text-2xl font-bold text-orange-600">
                        {timeEntries
                          .filter(entry => 
                            new Date(entry.date).getMonth() + 1 === selectedMonth &&
                            new Date(entry.date).getFullYear() === selectedYear &&
                            !entry.is_billable
                          )
                          .reduce((sum, entry) => sum + entry.hours, 0)
                          .toFixed(1)}h
                      </div>
                      <p className="text-xs text-muted-foreground">Non-billable Hours</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <div className="text-2xl font-bold">
                        {timeEntries
                          .filter(entry => 
                            new Date(entry.date).getMonth() + 1 === selectedMonth &&
                            new Date(entry.date).getFullYear() === selectedYear
                          )
                          .reduce((sum, entry) => sum + entry.hours, 0)
                          .toFixed(1)}h
                      </div>
                      <p className="text-xs text-muted-foreground">Total Hours</p>
                    </CardContent>
                  </Card>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

const TeamDashboard = ({ user, onLogout }) => {

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="border-b bg-white">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold text-slate-800">Project Hours</h1>
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-600">Welcome, {user.name}</span>
            <Button variant="outline" onClick={onLogout} data-testid="logout-btn">
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6">
        <MonthlyCalendar />
      </div>
    </div>
  );
};

const ProjectDetailCard = ({ project, projectHours, selectedMonth, selectedYear, apiClient }) => {
  const [showDetails, setShowDetails] = useState(false);
  const [projectData, setProjectData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchProjectDetails = async () => {
    if (showDetails && !projectData) {
      setLoading(true);
      try {
        const response = await apiClient.get(`/project-manager/monthly-data/${project.id}/${selectedYear}/${selectedMonth}`);
        setProjectData(response.data);
      } catch (err) {
        console.error("Error fetching project details:", err);
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchProjectDetails();
  }, [showDetails]);

  return (
    <div className="border rounded-lg p-4 hover:bg-gray-50">
      <div className="flex justify-between items-start mb-2">
        <h3 className="font-semibold text-lg">{project.name}</h3>
        <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded">
          {projectHours.toFixed(1)}h this month
        </span>
      </div>
      <p className="text-gray-600 text-sm mb-3">
        Client: {project.client_name}
      </p>
      <div className="text-xs text-gray-500 mb-3">
        Team: {project.assigned_user_ids.length} members assigned
      </div>
      
      <Button 
        variant="outline" 
        size="sm" 
        onClick={() => setShowDetails(!showDetails)}
        className="w-full"
      >
        {showDetails ? 'Hide Details' : 'View Team Hours'}
      </Button>

      {showDetails && (
        <div className="mt-4 border-t pt-4">
          {loading ? (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
            </div>
          ) : projectData ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium">Total Hours:</span> {projectData.total_hours.toFixed(1)}h
                </div>
                <div>
                  <span className="font-medium">Budget:</span> {projectData.budget_hours ? `${projectData.budget_hours}h` : 'No budget set'}
                </div>
              </div>
              
              <div className="space-y-3">
                <h4 className="font-medium text-sm">Team Member Hours:</h4>
                {Object.entries(projectData.user_data || {}).map(([userId, userData]) => (
                  <div key={userId} className="border rounded p-3 bg-gray-50">
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-medium text-sm">{userData.user_name}</span>
                      <span className="text-sm font-semibold">{userData.total_hours.toFixed(1)}h</span>
                    </div>
                    <div className="text-xs text-gray-600">
                      {Object.keys(userData.entries || {}).length} entries in {new Date(2024, selectedMonth - 1).toLocaleString('default', { month: 'long' })} {selectedYear}
                    </div>
                  </div>
                ))}
                {Object.keys(projectData.user_data || {}).length === 0 && (
                  <p className="text-sm text-gray-500">No time entries for this month.</p>
                )}
              </div>
            </div>
          ) : (
            <p className="text-sm text-red-500">Failed to load project details</p>
          )}
        </div>
      )}
    </div>
  );
};

const ProjectManagerDashboard = ({ user, onLogout }) => {
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [timeEntries, setTimeEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const apiClient = axios.create({
    baseURL: API,
    withCredentials: true,
    headers: {
      'Authorization': `Bearer ${getToken()}`
    }
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch projects (PM sees only their managed projects)
      const projectsResponse = await apiClient.get("/projects");
      setProjects(projectsResponse.data || []);
      
      // Fetch time entries (PM sees entries for their managed projects only)
      const timeEntriesResponse = await apiClient.get("/time-entries");
      setTimeEntries(timeEntriesResponse.data || []);
      
      // Fetch users for display purposes
      const usersResponse = await apiClient.get("/users");
      setUsers(usersResponse.data || []);
      
    } catch (err) {
      console.error("Error fetching data:", err);
      if (err.response?.status === 401) {
        onLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-slate-600">Loading your projects...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="border-b bg-white">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold text-slate-800">Project Manager Dashboard</h1>
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-600">Welcome, {user.name}</span>
            <Button variant="outline" onClick={onLogout}>
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-blue-600">
                {projects.length}
              </div>
              <p className="text-xs text-muted-foreground">Managed Projects</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-green-600">
                {timeEntries
                  .filter(entry => 
                    new Date(entry.date).getMonth() + 1 === selectedMonth &&
                    new Date(entry.date).getFullYear() === selectedYear
                  )
                  .reduce((sum, entry) => sum + entry.hours, 0)
                  .toFixed(1)}h
              </div>
              <p className="text-xs text-muted-foreground">Total Hours This Month</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-purple-600">
                {new Set(timeEntries
                  .filter(entry => 
                    new Date(entry.date).getMonth() + 1 === selectedMonth &&
                    new Date(entry.date).getFullYear() === selectedYear
                  )
                  .map(entry => entry.user_id)
                ).size}
              </div>
              <p className="text-xs text-muted-foreground">Active Team Members</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-orange-600">
                {timeEntries
                  .filter(entry => 
                    new Date(entry.date).getMonth() + 1 === selectedMonth &&
                    new Date(entry.date).getFullYear() === selectedYear &&
                    entry.is_billable
                  )
                  .reduce((sum, entry) => sum + entry.hours, 0)
                  .toFixed(1)}h
              </div>
              <p className="text-xs text-muted-foreground">Billable Hours</p>
            </CardContent>
          </Card>
        </div>

        {/* Project Manager Time Logging Section */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>My Time Tracking</CardTitle>
            <CardDescription>Log your own time entries for the projects you manage</CardDescription>
          </CardHeader>
          <CardContent>
            <MonthlyCalendar user={user} />
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>My Managed Projects</CardTitle>
              <CardDescription>Click on a project to view detailed team hours</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {projects.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">
                    No projects assigned to manage yet.
                  </p>
                ) : (
                  projects.map((project) => {
                    const projectHours = timeEntries
                      .filter(entry => 
                        entry.project_id === project.id &&
                        new Date(entry.date).getMonth() + 1 === selectedMonth &&
                        new Date(entry.date).getFullYear() === selectedYear
                      )
                      .reduce((sum, entry) => sum + entry.hours, 0);
                    
                    return (
                      <ProjectDetailCard 
                        key={project.id}
                        project={project}
                        projectHours={projectHours}
                        selectedMonth={selectedMonth}
                        selectedYear={selectedYear}
                        apiClient={apiClient}
                      />
                    );
                  })
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Recent Team Activity</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {timeEntries
                  .sort((a, b) => new Date(b.date) - new Date(a.date))
                  .slice(0, 10)
                  .map((entry) => {
                    const project = projects.find(p => p.id === entry.project_id);
                    const user = users.find(u => u.id === entry.user_id);
                    
                    if (!project || !user) return null;
                    
                    return (
                      <div key={entry.id} className="border-b pb-2 last:border-b-0">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="text-sm font-medium">{user.name}</p>
                            <p className="text-xs text-gray-600">{project.name}</p>
                            <p className="text-xs text-gray-500">{entry.description}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-semibold">{entry.hours}h</p>
                            <p className="text-xs text-gray-500">
                              {new Date(entry.date).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for stored user data
    const storedUser = localStorage.getItem("user");
    const token = localStorage.getItem("token");
    
    if (storedUser && token) {
      setUser(JSON.parse(storedUser));
    }
    setLoading(false);
  }, []);

  const handleLogin = (userData) => {
    setUser(userData);
  };

  const handleLogout = async () => {
    try {
      // Call backend logout endpoint to blacklist token
      await apiClient.post("/auth/logout");
    } catch (err) {
      console.error("Logout error:", err);
      // Continue with logout even if backend call fails
    } finally {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      setUser(null);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<PasswordReset />} />
        <Route 
          path="/*" 
          element={
            !user ? (
              <Login onLogin={handleLogin} />
            ) : user.role === "admin" ? (
              <AdminDashboard user={user} onLogout={handleLogout} />
            ) : user.role === "project_manager" ? (
              <ProjectManagerDashboard user={user} onLogout={handleLogout} />
            ) : (
              <TeamDashboard user={user} onLogout={handleLogout} />
            )
          } 
        />
      </Routes>
    </BrowserRouter>
  );
}

export default App;